<template>
  <div class="flex flex-col space-y-4 p-4">
    <div class="text-sm text-gray-400">
      Export your 3D scene to OBJ, STL, or GLTF/GLB formats for use in other applications.
    </div>

    <!-- Format Selection -->
    <div class="flex flex-col space-y-2">
      <label class="text-sm font-medium text-gray-300">Export Format</label>
      <DSelect
        v-model="selectedFormat"
        :options="exportFormats"
        optionLabel="label"
        optionValue="value"
        placeholder="Select export format"
        class="w-full" />
      <div v-if="selectedFormatInfo" class="text-xs text-gray-500">
        {{ selectedFormatInfo.description }}
      </div>
    </div>

    <!-- Filename Input -->
    <div class="flex flex-col space-y-2">
      <label class="text-sm font-medium text-gray-300">Filename</label>
      <DInputText v-model="filename" placeholder="Enter filename (without extension)" class="w-full" />
    </div>

    <!-- Export Options -->
    <div v-if="selectedFormat" class="flex flex-col space-y-3">
      <div class="text-sm font-medium text-gray-300">Export Options</div>

      <!-- GLTF/GLB Options -->
      <template v-if="['gltf', 'glb'].includes(selectedFormat)">
        <div class="flex items-center space-x-2">
          <DCheckbox v-model="options.embedImages" inputId="embedImages" :binary="true" />
          <label for="embedImages" class="text-sm text-gray-400"> Embed Images </label>
        </div>
        <div class="flex items-center space-x-2">
          <DCheckbox v-model="options.onlyVisible" inputId="onlyVisible" :binary="true" />
          <label for="onlyVisible" class="text-sm text-gray-400"> Export Only Visible Objects </label>
        </div>
      </template>

      <!-- STL Options -->
      <template v-if="selectedFormat === 'stl'">
        <div class="flex items-center space-x-2">
          <DCheckbox v-model="options.binary" inputId="stlBinary" :binary="true" />
          <label for="stlBinary" class="text-sm text-gray-400"> Binary Format (smaller file size) </label>
        </div>
      </template>

      <!-- OBJ Options -->
      <template v-if="selectedFormat === 'obj'">
        <div class="text-xs text-gray-500">Note: Materials and textures will be embedded in the .obj file</div>
      </template>
    </div>

    <!-- Export Progress -->
    <div v-if="exporting" class="flex items-center space-x-2 text-sm text-gray-400">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Exporting scene...</span>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="rounded bg-red-900/20 p-3 text-sm text-red-400">
      <i class="pi pi-exclamation-triangle mr-2"></i>
      {{ error }}
    </div>

    <!-- Success Message -->
    <div v-if="success" class="rounded bg-green-900/20 p-3 text-sm text-green-400">
      <i class="pi pi-check-circle mr-2"></i>
      Scene exported successfully!
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end space-x-2 border-t border-gray-700 pt-4">
      <DButton label="Cancel" @click="close" :disabled="exporting" />
      <DButton
        label="Export"
        severity="primary"
        icon="pi pi-download"
        @click="handleExport"
        :loading="exporting"
        :disabled="!canExport || exporting" />
    </div>
  </div>
</template>

<script setup>
import { useProjectStore } from '@/stores/api/project';
import { scene } from '@/threejs/core';
import { sceneExporter } from '@/threejs/utils/SceneExporter';
import { DButton, DCheckbox, DInputText, DSelect } from '@disguise/cloud-ui';
import { computed, inject, onBeforeUnmount, ref } from 'vue';

const dialogRef = inject('dialogRef');
const projectStore = useProjectStore();

let _closeTimer = null;
const selectedFormat = ref('obj');
const filename = ref('');
const exporting = ref(false);
const error = ref(null);
const success = ref(false);

// Export format options
const exportFormats = [
  {
    label: 'Wavefront OBJ (.obj)',
    value: 'obj',
    description: 'Standard 3D format, widely supported. Good for static models.',
  },
  {
    label: 'STL (.stl)',
    value: 'stl',
    description: 'Stereolithography format, commonly used for 3D printing and CAD.',
  },
  {
    label: 'GLTF (.gltf)',
    value: 'gltf',
    description: 'Modern JSON-based format with PBR materials. Industry standard for web 3D.',
  },
  {
    label: 'GLB (.glb)',
    value: 'glb',
    description: 'Binary GLTF format. Compact single-file version of GLTF.',
  },
];

// Export options based on format
const options = ref({
  binary: true, // For STL
  embedImages: true, // For GLTF/GLB
  onlyVisible: true, // For GLTF/GLB
  trs: false, // For GLTF/GLB
});

// Get info about selected format
const selectedFormatInfo = computed(() => {
  return exportFormats.find((f) => f.value === selectedFormat.value);
});

// Check if export is possible
const canExport = computed(() => {
  return selectedFormat.value && filename.value.trim().length > 0;
});

// Set default filename from project name
if (projectStore.project?.name) {
  filename.value = projectStore.project.name;
} else {
  filename.value = 'scene';
}

const handleExport = async () => {
  if (!canExport.value) return;

  exporting.value = true;
  error.value = null;
  success.value = false;

  try {
    // Prepare export options based on format
    const exportOptions = {};

    if (['gltf', 'glb'].includes(selectedFormat.value)) {
      exportOptions.embedImages = options.value.embedImages;
      exportOptions.onlyVisible = options.value.onlyVisible;
      exportOptions.trs = options.value.trs;
      exportOptions.binary = selectedFormat.value === 'glb';
    } else if (selectedFormat.value === 'stl') {
      exportOptions.binary = options.value.binary;
    }

    // Export and download
    await sceneExporter.exportAndDownload(scene, selectedFormat.value, filename.value, exportOptions);

    success.value = true;

    // Close dialog after short delay
    _closeTimer = setTimeout(() => {
      close();
    }, 1500);
  } catch (err) {
    console.error('Export error:', err);
    error.value = err.message || 'Failed to export scene. Please try again.';
  } finally {
    exporting.value = false;
  }
};

onBeforeUnmount(() => {
  if (_closeTimer) clearTimeout(_closeTimer);
});

const close = () => {
  if (dialogRef && dialogRef.value) {
    dialogRef.value.close();
  }
};
</script>
